/**
 * Live view of a PIN kind's throttle, for the screens that take a PIN.
 *
 * Pass the result of each `attemptPin` to `note` after a failed `attemptPin`; the countdown then ticks on its
 * own and clears itself when the time is up. The lock itself lives in local
 * settings (see pin.ts), so a reload picks it straight back up.
 */
import { useCallback, useEffect, useState } from 'react';
import { now } from '@shared/dates';
import { getPinLockedUntil, lockMessage, type PinAttempt, type PinKind } from './pin';

export function usePinLock(kind: PinKind) {
  const [lockedUntilMs, setLockedUntilMs] = useState<number | null>(null);
  const [, tick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void getPinLockedUntil(kind).then((until) => {
      if (!cancelled) setLockedUntilMs(until);
    });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  useEffect(() => {
    if (lockedUntilMs === null) return;
    const id = window.setInterval(() => {
      if (now().getTime() >= lockedUntilMs) setLockedUntilMs(null);
      else tick((n) => n + 1);
    }, 500);
    return () => window.clearInterval(id);
  }, [lockedUntilMs]);

  /** Fold an attempt result in; returns the message to show for a failure. */
  const note = useCallback((attempt: PinAttempt): string => {
    if (attempt.ok) {
      setLockedUntilMs(null);
      return '';
    }
    if (attempt.lockedUntilMs !== undefined) {
      setLockedUntilMs(attempt.lockedUntilMs);
      return lockMessage(attempt.lockedUntilMs);
    }
    const left = attempt.remainingAttempts;
    return left !== undefined && left <= 2
      ? `Wrong PIN — ${left} ${left === 1 ? 'try' : 'tries'} left`
      : 'Wrong PIN';
  }, []);

  const locked = lockedUntilMs !== null && now().getTime() < lockedUntilMs;
  return {
    locked,
    /** Shown while locked; recomputed on every tick. */
    lockText: locked && lockedUntilMs !== null ? lockMessage(lockedUntilMs) : '',
    note,
  };
}
